// IKPage.jsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EuroIcon from '@mui/icons-material/Euro';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PaidIcon from '@mui/icons-material/Paid';
import PreviewIcon from '@mui/icons-material/Preview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RouteIcon from '@mui/icons-material/Route';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';

import IKFormDialog from './IKFormDialog';
import IKRouteMap from './IKRouteMap';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtKm = (v) => (v != null ? `${Number(v).toFixed(2)} km` : '—');
const fmtEuro = (v) => (v != null ? `${Number(v).toFixed(2)} €` : '—');
const isApprovedIk = (entry) => entry?.status === 'approved';
const IK_STATUS_META = {
  draft: { label: 'Brouillon', color: 'default' },
  submitted: { label: 'Envoye', color: 'info' },
  approved: { label: 'Valide', color: 'success' },
};
const getEasyRedmineTicketUrl = (ticketId) => `https://toolbox.easyredmine.com/issues/${ticketId}`;
const getEasyRedmineTimeEntryUrl = (timeEntryId) => `https://toolbox.easyredmine.com/time_entries/${timeEntryId}/edit`;

function SummaryCard({ label, value, icon, accent, softBg, subValue }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        flex: 1,
        borderRadius: 3,
        borderColor: accent,
        background: `linear-gradient(135deg, ${softBg} 0%, #ffffff 100%)`,
        minHeight: 108,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ mt: 1, color: accent, fontWeight: 700 }}>
            {value}
          </Typography>
          {subValue ? (
            <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', fontWeight: 600 }}>
              {subValue}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: accent,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 10px 20px ${accent}33`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}
function toInputDateValue(date) {
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
}

function buildDefaultIkPeriod(referenceDate = new Date()) {
  const currentYear = referenceDate.getFullYear();
  const currentMonth = referenceDate.getMonth();
  const currentDay = referenceDate.getDate();
  const startMonthOffset = currentDay >= 24 ? 0 : -1;
  const endMonthOffset = currentDay >= 24 ? 1 : 0;
  const start = new Date(currentYear, currentMonth + startMonthOffset, 24, 0, 0, 0, 0);
  const end = new Date(currentYear, currentMonth + endMonthOffset, 23, 23, 59, 59, 999);
  return {
    start,
    end,
    startDate: toInputDateValue(start),
    endDate: toInputDateValue(end),
  };
}

function buildSelectedPeriod(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;
  return { start, end };
}

export default function IK() {
  const defaultPeriod = useMemo(() => buildDefaultIkPeriod(), []);
  const [entries, setEntries] = useState([]);
  const [periodTimeEntries, setPeriodTimeEntries] = useState([]);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [meConfig, setMeConfig] = useState({
    fiscalCv: null,
    mainAddress: null,
    role: 'client',
    staff: null,
    canAccessIk: false,
    canAccessIkGlobal: false,
    canApproveIk: false,
  });
  const [configLoaded, setConfigLoaded] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(defaultPeriod.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(defaultPeriod.endDate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [routeRow, setRouteRow] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptRow, setReceiptRow] = useState(null);
  const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const selectedPeriod = useMemo(
    () => buildSelectedPeriod(selectedStartDate, selectedEndDate),
    [selectedEndDate, selectedStartDate]
  );

  const loadEntries = useCallback(async () => {
    if (!selectedPeriod) {
      setEntries([]);
      setLoading(false);
      setError('Période invalide.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/api/ik', {
        params: {
          start: selectedPeriod.start.toISOString(),
          end: selectedPeriod.end.toISOString(),
        },
      });
      setEntries(data?.data || []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  const loadPeriod = useCallback(async () => {
    if (!selectedPeriod) {
      setPeriodTimeEntries([]);
      setTicketOptions([]);
      return;
    }
    const { data } = await api.get('/api/ik/period/tickets-time', {
      params: {
        deplacementOnly: true,
        start: selectedPeriod.start.toISOString(),
        end: selectedPeriod.end.toISOString(),
      },
    });
    setPeriodTimeEntries(data?.timeEntries || []);
    setTicketOptions(
      (data?.tickets || []).map((t) => ({
        value: t.ticketId,
        label: `#${t.ticketId} - ${t.subject || 'Sans sujet'}`,
      }))
    );
  }, [selectedPeriod]);

  const loadAddresses = useCallback(async () => {
    const { data } = await api.get('/api/ik/address-options');
    setAddressOptions(data?.data || []);
  }, []);

  const loadMeConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/api/ik/me-config');
      setMeConfig(data?.data || {
        fiscalCv: null,
        mainAddress: null,
        role: 'client',
        staff: null,
        canAccessIk: false,
        canAccessIkGlobal: false,
        canApproveIk: false,
      });
    } finally {
      setConfigLoaded(true);
    }
  }, []);

  const submitEntry = useCallback(async (entryId) => {
    await api.post(`/api/ik/${entryId}/submit`);
    await loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadMeConfig();
  }, [loadMeConfig]);

  useEffect(() => {
    if (!configLoaded || !meConfig?.canAccessIk) return;
    loadEntries();
    loadPeriod();
    loadAddresses();
  }, [configLoaded, loadAddresses, loadEntries, loadPeriod, meConfig?.canAccessIk]);

  const downloadExpenseReceipt = useCallback(async (entryId, expenseIndex, fileName) => {
    try {
      const response = await api.get(`/api/ik/${entryId}/expenses/${expenseIndex}/receipt`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || `justificatif-${expenseIndex + 1}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Impossible de telecharger le justificatif.');
    }
  }, []);

  const previewExpenseReceipt = useCallback(async (entryId, expenseIndex, fileName) => {
    try {
      const response = await api.get(`/api/ik/${entryId}/expenses/${expenseIndex}/receipt`, {
        responseType: 'blob',
      });
      const mimeType = response.headers?.['content-type'] || response.data?.type || 'application/octet-stream';
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(blob);

      setReceiptPreview((current) => {
        if (current?.url) {
          window.URL.revokeObjectURL(current.url);
        }
        return {
          url: blobUrl,
          mimeType,
          fileName: fileName || `justificatif-${expenseIndex + 1}`,
          entryId,
          expenseIndex,
        };
      });
      setReceiptPreviewOpen(true);
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Impossible d afficher le justificatif.');
    }
  }, []);

  const totals = useMemo(() => {
    return entries.reduce(
      (acc, entry) => {
        acc.distanceKm += Number(entry?.distanceKm || 0);
        acc.distanceAmount += Number(entry?.amount || 0);
        acc.extraExpensesAmount += Number(entry?.extraExpensesAmount || 0);
        if (isApprovedIk(entry)) {
          acc.extraExpensesReimbursedAmount += Number(entry?.extraExpensesReimbursedAmount || 0);
          acc.totalReimbursedAmount += Number((entry?.totalReimbursedAmount ?? entry?.amount) || 0);
        }
        return acc;
      },
      {
        distanceKm: 0,
        distanceAmount: 0,
        extraExpensesAmount: 0,
        extraExpensesReimbursedAmount: 0,
        totalReimbursedAmount: 0,
      }
    );
  }, [entries]);
  const stats = useMemo(() => {
    const existingTimeEntryIds = new Set(
      entries
        .map((entry) => Number(entry?.timeEntryId))
        .filter((timeEntryId) => Number.isFinite(timeEntryId))
    );
    const availableTimeEntryIds = new Set(
      periodTimeEntries
        .map((timeEntry) => Number(timeEntry?.entryId))
        .filter((timeEntryId) => Number.isFinite(timeEntryId))
    );

    let unsavedCount = 0;
    availableTimeEntryIds.forEach((timeEntryId) => {
      if (!existingTimeEntryIds.has(timeEntryId)) {
        unsavedCount += 1;
      }
    });

    return {
      draftCount: entries.filter((entry) => entry?.status === 'draft').length,
      approvedCount: entries.filter((entry) => entry?.status === 'approved').length,
      approvedDistanceKm: entries
        .filter((entry) => entry?.status === 'approved')
        .reduce((sum, entry) => sum + Number(entry?.distanceKm || 0), 0),
      unsavedCount,
    };
  }, [entries, periodTimeEntries]);

  const columns = useMemo(
    () => [
      { accessorKey: 'dateTrajet', header: 'Date', Cell: ({ row }) => fmtDate(row.original.dateTrajet) },
      {
        accessorKey: 'ticketId',
        header: 'Ticket',
        Cell: ({ row }) => {
          const ticketId = row.original.ticketId;

          if (!ticketId) return '—';

          return (
            <Typography
              variant="body2"
              noWrap
              component="a"
              href={getEasyRedmineTicketUrl(ticketId)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              #{ticketId}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'timeEntryId',
        header: 'Temps passé',
        Cell: ({ row }) => {
          const timeEntryId = row.original.timeEntryId;

          if (!timeEntryId) return '—';

          return (
            <Typography
              variant="body2"
              noWrap
              component="a"
              href={getEasyRedmineTimeEntryUrl(timeEntryId)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              #{timeEntryId}
            </Typography>
          );
        },
      },
      { accessorKey: 'projectName', header: 'Projet' },
      { accessorKey: 'clientOrganizationName', header: 'Client' },
      {
        accessorKey: 'status',
        header: 'Statut',
        Cell: ({ row }) => {
          const meta = IK_STATUS_META[row.original.status] || { label: row.original.status || 'Inconnu', color: 'default' };
          return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />;
        },
      },
      { accessorKey: 'distanceKm', header: 'Distance', Cell: ({ row }) => fmtKm(row.original.distanceKm) },
      { accessorKey: 'roundTrip', header: 'A/R', Cell: ({ row }) => (row.original.roundTrip ? 'Oui' : 'Non') },
      { accessorKey: 'amount', header: 'Déplacement', Cell: ({ row }) => fmtEuro(row.original.amount) },
      {
        accessorKey: 'extraExpensesAmount',
        header: 'Frais supp.',
        Cell: ({ row }) => {
          const amount = Number(row.original.extraExpensesAmount || 0);
          if (amount <= 0) return '—';

          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {fmtEuro(amount)}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'totalReimbursedAmount',
        header: 'Total remboursé',
        Cell: ({ row }) => fmtEuro(row.original.totalReimbursedAmount ?? row.original.amount),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Voir le detail">
              <IconButton
                size="small"
                onClick={() => {
                  setDetailRow(row.original);
                  setDetailDialogOpen(true);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Display trajet">
              <IconButton
                size="small"
                onClick={() => {
                  setRouteRow(row.original);
                  setRouteDialogOpen(true);
                }}
              >
                <RouteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Modifier">
              <span>
                <IconButton
                  size="small"
                  disabled={row.original.status !== 'draft'}
                  onClick={() => {
                    setEditing(row.original);
                    setFormOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Justificatifs">
              <span>
                <IconButton
                  size="small"
                  color={row.original?.expenses?.some((expense) => expense?.receiptPath) ? 'secondary' : 'default'}
                  disabled={!row.original?.expenses?.some((expense) => expense?.receiptPath)}
                  onClick={() => {
                    setReceiptRow(row.original);
                    setReceiptDialogOpen(true);
                  }}
                  sx={row.original?.expenses?.some((expense) => expense?.receiptPath)
                    ? {
                        bgcolor: 'secondary.main',
                        color: '#fff',
                        '&:hover': { bgcolor: 'secondary.dark' },
                      }
                    : undefined}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Envoyer">
              <span>
                <IconButton
                  size="small"
                  color="info"
                  disabled={row.original.status !== 'draft'}
                  onClick={async () => {
                    if (!window.confirm('Envoyer cette IK ? Elle ne sera plus modifiable.')) return;
                    await submitEntry(row.original._id);
                  }}
                  sx={row.original.status === 'draft'
                    ? {
                        bgcolor: 'info.main',
                        color: '#fff',
                        '&:hover': { bgcolor: 'info.dark' },
                      }
                    : undefined}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={row.original.status !== 'draft'}
                  onClick={async () => {
                    if (!window.confirm('Supprimer cette entrée ?')) return;
                    await api.delete(`/api/ik/${row.original._id}`);
                    loadEntries();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [loadEntries, submitEntry]
  );

  const table = useMaterialReactTable({
    columns,
    data: entries,
    state: { isLoading: loading },
    enablePagination: false,
    initialState: { density: 'compact' },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return (
    <Box>
      {configLoaded && !meConfig?.canAccessIk ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Accès refusé
          </Typography>
          <Typography color="text.secondary">
            La page Mes IK est réservée aux profils `Team` et `Direction`.
          </Typography>
        </Paper>
      ) : null}
      {!configLoaded || !meConfig?.canAccessIk ? null : (
        <>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6">IK</Typography>
          {selectedPeriod && (
            <Typography variant="caption" color="text.secondary">
              Période: {fmtDate(selectedPeriod.start)} → {fmtDate(selectedPeriod.end)}
            </Typography>
          )}
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={`CV fiscaux: ${meConfig?.fiscalCv ?? 'Non renseigné'}`}
              color={meConfig?.fiscalCv ? 'primary' : 'default'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Adresse principale: ${meConfig?.mainAddress || 'Non renseignée'}`}
              color={meConfig?.mainAddress ? 'primary' : 'default'}
            />
          </Stack>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            Nouvelle IK
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          borderColor: '#cfe0f5',
          background: 'linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: '#dbeafe',
              color: '#1d4ed8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TuneIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Filtres
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Période personnalisée de consultation.
            </Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Date début"
            type="date"
            value={selectedStartDate}
            onChange={(event) => setSelectedStartDate(event.target.value)}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <CalendarMonthIcon fontSize="small" sx={{ color: '#2563eb', mr: 1 }} />,
            }}
          />
          <TextField
            label="Date fin"
            type="date"
            value={selectedEndDate}
            onChange={(event) => setSelectedEndDate(event.target.value)}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: <CalendarMonthIcon fontSize="small" sx={{ color: '#2563eb', mr: 1 }} />,
            }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedStartDate(defaultPeriod.startDate);
              setSelectedEndDate(defaultPeriod.endDate);
            }}
          >
            Réinitialiser 24 → 23
          </Button>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Fenêtre appliquée
            </Typography>
            <Typography variant="body2">
              {selectedPeriod ? `${fmtDate(selectedPeriod.start)} → ${fmtDate(selectedPeriod.end)}` : 'Période invalide'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
        <SummaryCard
          label="IK brouillons"
          value={stats.draftCount}
          icon={<EditIcon fontSize="small" />}
          accent="#475467"
          softBg="#f8fafc"
        />
        <SummaryCard
          label="IK non saisies"
          value={stats.unsavedCount}
          icon={<HourglassTopIcon fontSize="small" />}
          accent="#d97706"
          softBg="#fff7ed"
        />
        <SummaryCard
          label="IK validées"
          value={stats.approvedCount}
          subValue={fmtKm(stats.approvedDistanceKm)}
          icon={<AssignmentTurnedInIcon fontSize="small" />}
          accent="#15803d"
          softBg="#ecfdf3"
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
        <SummaryCard
          label="KM parcourus"
          value={fmtKm(totals.distanceKm)}
          icon={<RouteIcon fontSize="small" />}
          accent="#2563eb"
          softBg="#eff6ff"
        />
        <SummaryCard
          label="Total déplacement"
          value={fmtEuro(totals.distanceAmount)}
          icon={<DirectionsCarIcon fontSize="small" />}
          accent="#0f766e"
          softBg="#ecfeff"
        />
        <SummaryCard
          label="Frais saisis"
          value={fmtEuro(totals.extraExpensesAmount)}
          icon={<EuroIcon fontSize="small" />}
          accent="#7c3aed"
          softBg="#f5f3ff"
        />
        <SummaryCard
          label="Frais remboursés"
          value={fmtEuro(totals.extraExpensesReimbursedAmount)}
          icon={<PaidIcon fontSize="small" />}
          accent="#be185d"
          softBg="#fdf2f8"
        />
        <SummaryCard
          label="Total remboursé"
          value={fmtEuro(totals.totalReimbursedAmount)}
          icon={<AssignmentTurnedInIcon fontSize="small" />}
          accent="#1d4ed8"
          softBg="#eff6ff"
        />
      </Stack>

      {error ? (
        <Paper sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <MaterialReactTable table={table} />
      )}

      <IKFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          loadEntries();
          loadPeriod();
          loadMeConfig();
        }}
        ticketOptions={ticketOptions}
        periodTimeEntries={periodTimeEntries}
        existingEntries={entries}
        addressOptions={addressOptions}
        meConfig={meConfig}
        editing={editing}
        api={api}
      />

      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6">Detail IK</Typography>
              <Typography variant="body2" color="text.secondary">
                {detailRow?.clientOrganizationName || 'Client non renseigne'} • {fmtDate(detailRow?.dateTrajet)}
              </Typography>
            </Box>
            {detailRow?.status ? (
              <Chip
                size="small"
                variant="outlined"
                label={IK_STATUS_META[detailRow.status]?.label || detailRow.status}
                color={IK_STATUS_META[detailRow.status]?.color || 'default'}
              />
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Informations
                </Typography>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">Ticket:</Typography>
                    {detailRow?.ticketId ? (
                      <Typography
                        variant="body2"
                        component="a"
                        href={getEasyRedmineTicketUrl(detailRow.ticketId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        #{detailRow.ticketId}
                      </Typography>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2">Temps passe:</Typography>
                    {detailRow?.timeEntryId ? (
                      <Typography
                        variant="body2"
                        component="a"
                        href={getEasyRedmineTimeEntryUrl(detailRow.timeEntryId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        #{detailRow.timeEntryId}
                      </Typography>
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </Stack>
                  <Typography variant="body2">Projet: {detailRow?.projectName || '—'}</Typography>
                  <Typography variant="body2">Client: {detailRow?.clientOrganizationName || '—'}</Typography>
                  <Typography variant="body2">Date trajet: {fmtDate(detailRow?.dateTrajet)}</Typography>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Remboursement
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2">Distance: {fmtKm(detailRow?.distanceKm)}</Typography>
                  <Typography variant="body2">Aller-retour: {detailRow?.roundTrip ? 'Oui' : 'Non'}</Typography>
                  <Typography variant="body2">Montant deplacement: {fmtEuro(detailRow?.amount)}</Typography>
                  <Typography variant="body2">Frais supplementaires rembourses: {fmtEuro(detailRow?.extraExpensesReimbursedAmount)}</Typography>
                  <Typography variant="subtitle2">Total rembourse: {fmtEuro(detailRow?.totalReimbursedAmount ?? detailRow?.amount)}</Typography>
                </Stack>
              </Paper>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Trajet
              </Typography>
              <IKRouteMap
                geometry={detailRow?.mapboxRouteGeometry}
                points={(detailRow?.points || []).map((point) => ({
                  ...point,
                  organizationName: point?.organizationName
                    || (point?.selectionType === 'client' ? detailRow?.clientOrganizationName : ''),
                }))}
                height={360}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Points du trajet
              </Typography>
              <Stack spacing={1}>
                {(detailRow?.points || []).map((point, index) => (
                  <Paper key={`${detailRow?._id || 'detail'}-point-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                      <Box>
                        {(() => {
                          const relatedOrganizationName = point.organizationName
                            || (point.selectionType === 'client' ? detailRow?.clientOrganizationName : '');

                          return (
                            <>
                        <Typography variant="body2" fontWeight={600}>
                          {point.label || `Point ${index + 1}`}
                        </Typography>
                        {relatedOrganizationName ? (
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                            {relatedOrganizationName}{point.siteName ? ` • ${point.siteName}` : ''}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary">
                          {point.address || 'Adresse non renseignee'}
                        </Typography>
                            </>
                          );
                        })()}
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={point.selectionType || point.sourceType || 'manual'}
                        color="primary"
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Frais supplementaires
                </Typography>
                <Stack spacing={1}>
                  {(detailRow?.expenses || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Aucun frais supplementaire.
                    </Typography>
                  ) : (
                    (detailRow?.expenses || []).map((expense, expenseIndex) => (
                      <Paper key={`${detailRow?._id || 'detail'}-expense-${expenseIndex}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {expense.type === 'meal' ? 'Repas' : 'Parc metre'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Paye: {fmtEuro(expense.amountPaid)} • Rembourse: {fmtEuro(expense.reimbursedAmount)}
                            </Typography>
                          </Box>
                          {expense.receiptPath ? (
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PreviewIcon />}
                                onClick={() => previewExpenseReceipt(
                                  detailRow._id,
                                  expenseIndex,
                                  expense.receiptOriginalName || `justificatif-${expenseIndex + 1}`
                                )}
                              >
                                Apercu
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => downloadExpenseReceipt(
                                  detailRow._id,
                                  expenseIndex,
                                  expense.receiptOriginalName || `justificatif-${expenseIndex + 1}`
                                )}
                              >
                                Piece jointe
                              </Button>
                            </Stack>
                          ) : (
                            <Chip size="small" variant="outlined" label="Sans piece jointe" />
                          )}
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, flex: 1, borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" color={detailRow?.notes ? 'text.primary' : 'text.secondary'}>
                  {detailRow?.notes || 'Aucune note.'}
                </Typography>
              </Paper>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routeDialogOpen} onClose={() => setRouteDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Trajet IK</DialogTitle>
        <DialogContent dividers>
          <IKRouteMap geometry={routeRow?.mapboxRouteGeometry} points={routeRow?.points || []} height={460} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiptDialogOpen} onClose={() => setReceiptDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Justificatifs de frais</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(receiptRow?.expenses || []).filter((expense) => expense?.receiptPath).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aucun justificatif disponible pour cette IK.
              </Typography>
            ) : (
              (receiptRow?.expenses || [])
                .map((expense, expenseIndex) => ({ expense, expenseIndex }))
                .filter(({ expense }) => expense?.receiptPath)
                .map(({ expense, expenseIndex }) => (
                  <Paper key={`${receiptRow?._id}-${expenseIndex}`} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {expense.type === 'meal' ? 'Repas' : 'Parc metre'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {expense.receiptOriginalName || `Justificatif ${expenseIndex + 1}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={() => previewExpenseReceipt(
                          receiptRow._id,
                          expenseIndex,
                          expense.receiptOriginalName || `justificatif-${expenseIndex + 1}`
                        )}
                      >
                        Apercu
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadExpenseReceipt(
                          receiptRow._id,
                          expenseIndex,
                          expense.receiptOriginalName || `justificatif-${expenseIndex + 1}`
                        )}
                      >
                        Telecharger
                      </Button>
                    </Stack>
                  </Paper>
                ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiptDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={receiptPreviewOpen}
        onClose={() => {
          setReceiptPreviewOpen(false);
          setReceiptPreview((current) => {
            if (current?.url) {
              window.URL.revokeObjectURL(current.url);
            }
            return null;
          });
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{receiptPreview?.fileName || 'Apercu justificatif'}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {!receiptPreview?.url ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Aucun fichier a afficher.
              </Typography>
            </Box>
          ) : receiptPreview.mimeType.startsWith('image/') ? (
            <Box
              component="img"
              src={receiptPreview.url}
              alt={receiptPreview.fileName || 'Justificatif'}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                bgcolor: '#f5f5f5',
              }}
            />
          ) : receiptPreview.mimeType === 'application/pdf' ? (
            <Box
              component="iframe"
              src={receiptPreview.url}
              title={receiptPreview.fileName || 'Justificatif PDF'}
              sx={{ width: '100%', height: '80vh', border: 0 }}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Apercu non disponible pour ce type de fichier.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadExpenseReceipt(
                  receiptPreview.entryId,
                  receiptPreview.expenseIndex,
                  receiptPreview.fileName
                )}
              >
                Telecharger le fichier
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setReceiptPreviewOpen(false);
            setReceiptPreview((current) => {
              if (current?.url) {
                window.URL.revokeObjectURL(current.url);
              }
              return null;
            });
          }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
}
